import { createStore } from "zustand-x";

export type DialogType = "confirm" | "alert" | "prompt";

export type Dialog = {
  type: DialogType;
  title: string;
  message: string;
  label?: string;
  options: DialogOptions;
  resolve: (value: string, result?: string) => void;
  reject: (reason?: unknown) => void;
};

export type DialogOptions = {
  cancelText?: string;
  confirmText?: string;
  defaultInput?: string;
};

type InteractionStore = {
  dialog: Dialog | null;
  /**
   * Add a dialog to the store
   * @param type - The type of dialog to display
   * @param title - The title of the dialog
   * @param message - The message to display
   * @param options - The options to pass to the dialog
   * @returns Promise that resolves with the dialog result
   */
  addDialog: (
    type: DialogType,
    title: string,
    message: string,
    options?: DialogOptions
  ) => Promise<string>;
  /**
   * Close the current dialog, this is generally for confirmations
   * @param value - The value to resolve the dialog with
   * @param result - Optional result value (used for prompt dialogs)
   */
  closeDialog: (value: "confirmed" | "canceled", result?: string) => void;
  /**
   * Cancel the current dialog
   */
  cancelDialog: () => void;
  /**
   * Display a confirm dialog
   * @param title - The title of the dialog
   * @param message - The message to display
   * @param options - The options to pass to the dialog
   * @returns Promise that resolves with the dialog result
   */
  confirm: (title: string, message: string, options?: DialogOptions) => Promise<string>;
  /**
   * Display an alert dialog
   * @param title - The title of the dialog
   * @param message - The message to display
   * @param options - The options to pass to the dialog
   * @returns Promise that resolves with the dialog result
   */
  alert: (title: string, message: string, options?: DialogOptions) => Promise<string>;
  /**
   * Display a prompt dialog
   * @param title - The title of the dialog
   * @param message - The message to display
   * @param options - The options to pass to the dialog
   * @returns Promise that resolves with the dialog result
   */
  prompt: (title: string, message: string, options?: DialogOptions) => Promise<string>;
};

// Create the store with initial state and actions
const interactionStore = createStore<InteractionStore>(
  (set, get) => ({
    dialog: null,

    addDialog: (
      type: DialogType,
      title: string,
      message: string,
      options: DialogOptions = {}
    ): Promise<string> => {
      return new Promise<string>((resolve, reject) => {
        set({
          dialog: {
            type,
            title,
            message,
            options,
            resolve,
            reject,
          },
        });
      });
    },

    closeDialog: (value: "confirmed" | "canceled", result?: string) => {
      const currentDialog = get().dialog;
      if (currentDialog) {
        currentDialog.resolve(result || value);
        set({ dialog: null });
      }
    },

    cancelDialog: () => {
      const currentDialog = get().dialog;
      if (currentDialog) {
        currentDialog.reject();
        set({ dialog: null });
      }
    },

    confirm: (title: string, message: string, options: DialogOptions = {}) => {
      return get().addDialog("confirm", title, message, options);
    },

    alert: (title: string, message: string, options: DialogOptions = {}) => {
      return get().addDialog("alert", title, message, options);
    },

    prompt: (title: string, message: string, options: DialogOptions = {}) => {
      return get().addDialog("prompt", title, message, options);
    },
  }),
  {
    name: "InteractionStore",
    devtools: true,
  }
);

export default interactionStore;

